// Map a free-text color/finish name (as it appears on the order) to a
// printable swatch color. Cabinet Depot color names are not standardized, so
// we look for the most descriptive recognizable word in the string and fall
// back to neutral gray when nothing matches.
//
// Used on printed labels (lib/labelHtml.js) — the swatch is a small filled
// circle rendered next to the color text. The mapping favors visual
// identifiability over color-theory precision (e.g. "Espresso" → dark brown
// rather than a true coffee brown).

const COLOR_TABLE = [
  // Whites / off-whites
  { match: /(linen|antique\s*white|off\s*white|cream|alabaster|ivory)/i, hex: "#F5EFE0" },
  { match: /\bwhite\b/i, hex: "#FFFFFF" },
  // Grays
  { match: /(charcoal|graphite|slate)/i, hex: "#36454F" },
  { match: /(barnwood|driftwood|weathered)/i, hex: "#8A8278" },
  { match: /\bgrey|\bgray/i, hex: "#9CA3AF" },
  // Blacks
  { match: /(jet\s*black|onyx|midnight|matte\s*black)/i, hex: "#0B0B0F" },
  { match: /\bblack\b/i, hex: "#1A1A1A" },
  // Browns / wood
  { match: /(espresso|java)/i, hex: "#3B2A20" },
  { match: /(mocha|chocolate|cocoa)/i, hex: "#4E342E" },
  { match: /(walnut|hickory|chestnut)/i, hex: "#6B4226" },
  { match: /(cherry|mahogany|rosewood)/i, hex: "#6B2D2D" },
  { match: /(oak|honey|wheat|amber|caramel)/i, hex: "#C68642" },
  { match: /(maple|natural|birch|beech|pecan)/i, hex: "#D4A574" },
  { match: /(sand|tan|beige|khaki|wheat|almond|biscuit)/i, hex: "#D2B48C" },
  { match: /\bbrown\b/i, hex: "#6B4226" },
  // Reds / oranges
  { match: /(burgundy|wine|crimson|merlot)/i, hex: "#6B1A2B" },
  { match: /\bred\b/i, hex: "#C8102E" },
  { match: /\borange\b/i, hex: "#E07A1F" },
  // Yellows
  { match: /(gold|mustard|amber)/i, hex: "#D4A017" },
  { match: /\byellow\b/i, hex: "#F2C84B" },
  // Greens
  { match: /(sage|olive)/i, hex: "#7B8F5E" },
  { match: /(forest|hunter|emerald|pine|moss)/i, hex: "#2F5233" },
  { match: /\bgreen\b/i, hex: "#3F8D3F" },
  // Blues
  { match: /(navy|royal|midnight\s*blue|indigo)/i, hex: "#1F2D5C" },
  { match: /(teal|sea|aqua|turquoise)/i, hex: "#1B7A7E" },
  { match: /\bblue\b/i, hex: "#3B6EA5" },
  // Misc
  { match: /\bpurple|\bviolet|\bplum/i, hex: "#5E3370" },
  { match: /\bpink|\brose|\bblush/i, hex: "#E89AB0" },
];

/**
 * Resolve a color string to a hex value. Returns `null` when the input is
 * empty or unrecognized — caller can decide to omit the swatch entirely
 * rather than show a misleading gray dot.
 */
export function resolveColorSwatch(name) {
  if (!name || typeof name !== "string") return null;
  const txt = name.trim();
  if (!txt) return null;
  // Accept literal hex codes (#RGB, #RRGGBB) and short HTML color words.
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(txt)) return txt;
  for (const { match, hex } of COLOR_TABLE) {
    if (match.test(txt)) return hex;
  }
  return null;
}

/**
 * Pick a contrasting text color (#000 or #fff) for placing text on top of
 * the given swatch hex. Uses the WCAG relative-luminance formula.
 */
export function swatchTextColor(hex) {
  if (!hex || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return "#000";
  let h = hex.slice(1);
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  // Simple perceived luminance — adequate for print contrast decisions.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 0.55 ? "#000" : "#fff";
}

// Cabinet Depot SKUs encode the finish/color as the second dash-separated
// segment, e.g. `ESS-WHT-W2130` → WHT = White. When the `item.color` field is
// empty on an order we fall back to this lookup so the label still shows the
// correct swatch + text.
//
// Keys are uppercase. If you need to add new codes, the format is
// `XYZ: { name: "Friendly Name", hex: "#RRGGBB" }`.
const SKU_COLOR_CODES = {
  WHT: { name: "White", hex: "#FFFFFF" },
  WHI: { name: "White", hex: "#FFFFFF" },
  ANT: { name: "Antique White", hex: "#F5EFE0" },
  LNN: { name: "Linen", hex: "#F0E9D6" },
  LIN: { name: "Linen", hex: "#F0E9D6" },
  CRM: { name: "Cream", hex: "#F0E9D6" },
  IVR: { name: "Ivory", hex: "#FFFFF0" },
  ALM: { name: "Almond", hex: "#EFDECD" },

  BLK: { name: "Black", hex: "#1A1A1A" },
  ONX: { name: "Onyx", hex: "#0B0B0F" },
  CHR: { name: "Charcoal", hex: "#36454F" },
  GRY: { name: "Grey", hex: "#9CA3AF" },
  GRA: { name: "Graphite", hex: "#36454F" },
  SLT: { name: "Slate", hex: "#36454F" },
  BAR: { name: "Barnwood", hex: "#8A8278" },
  DRF: { name: "Driftwood", hex: "#8A8278" },

  ESP: { name: "Espresso", hex: "#3B2A20" },
  ESS: { name: "Espresso", hex: "#3B2A20" },
  JAV: { name: "Java", hex: "#3B2A20" },
  MCH: { name: "Mocha", hex: "#4E342E" },
  MOC: { name: "Mocha", hex: "#4E342E" },
  CHC: { name: "Chocolate", hex: "#4E342E" },
  CHO: { name: "Chocolate", hex: "#4E342E" },
  COC: { name: "Cocoa", hex: "#4E342E" },
  WAL: { name: "Walnut", hex: "#6B4226" },
  WLN: { name: "Walnut", hex: "#6B4226" },
  HCK: { name: "Hickory", hex: "#6B4226" },
  CHE: { name: "Cherry", hex: "#6B2D2D" },
  MAH: { name: "Mahogany", hex: "#6B2D2D" },
  OAK: { name: "Oak", hex: "#C68642" },
  HON: { name: "Honey", hex: "#C68642" },
  HNY: { name: "Honey", hex: "#C68642" },
  AMB: { name: "Amber", hex: "#C68642" },
  CAR: { name: "Caramel", hex: "#C68642" },
  MAP: { name: "Maple", hex: "#D4A574" },
  NAT: { name: "Natural", hex: "#D4A574" },
  BIR: { name: "Birch", hex: "#D4A574" },
  PEC: { name: "Pecan", hex: "#D4A574" },
  SND: { name: "Sand", hex: "#D2B48C" },
  TAN: { name: "Tan", hex: "#D2B48C" },
  BEI: { name: "Beige", hex: "#D2B48C" },
  KHK: { name: "Khaki", hex: "#D2B48C" },
  BRN: { name: "Brown", hex: "#6B4226" },

  RED: { name: "Red", hex: "#C8102E" },
  BUR: { name: "Burgundy", hex: "#6B1A2B" },
  ORG: { name: "Orange", hex: "#E07A1F" },
  GLD: { name: "Gold", hex: "#D4A017" },
  YEL: { name: "Yellow", hex: "#F2C84B" },
  MUS: { name: "Mustard", hex: "#D4A017" },

  GRN: { name: "Green", hex: "#3F8D3F" },
  GRE: { name: "Green", hex: "#3F8D3F" },
  SAG: { name: "Sage", hex: "#7B8F5E" },
  OLV: { name: "Olive", hex: "#7B8F5E" },
  FOR: { name: "Forest", hex: "#2F5233" },
  HNT: { name: "Hunter", hex: "#2F5233" },
  PIN: { name: "Pine", hex: "#2F5233" },
  MOS: { name: "Moss", hex: "#2F5233" },

  BLU: { name: "Blue", hex: "#3B6EA5" },
  NAV: { name: "Navy", hex: "#1F2D5C" },
  IND: { name: "Indigo", hex: "#1F2D5C" },
  TEA: { name: "Teal", hex: "#1B7A7E" },
  AQU: { name: "Aqua", hex: "#1B7A7E" },

  PUR: { name: "Purple", hex: "#5E3370" },
  PNK: { name: "Pink", hex: "#E89AB0" },
  ROS: { name: "Rose", hex: "#E89AB0" },
};

/**
 * Extract the 3-letter color code from a SKU like `ESS-WHT-W2130`. Returns
 * `{ name, hex }` if recognized, else `null`. Tolerates 2-4 letter segments
 * (some SKUs use shorter codes) and ignores numeric segments.
 */
export function resolveColorFromSku(sku) {
  if (!sku || typeof sku !== "string") return null;
  const parts = sku.toUpperCase().split(/[-_]/);
  // Skip the first segment (brand/series) and look at subsequent
  // alphabetic-only segments for a color code match.
  for (let i = 1; i < parts.length; i++) {
    const p = parts[i];
    if (!/^[A-Z]{2,4}$/.test(p)) continue;
    if (SKU_COLOR_CODES[p]) return SKU_COLOR_CODES[p];
  }
  return null;
}

/** Strict `#RGB` or `#RRGGBB` hex validator. Used to guard `custom_paint_hex`
 *  values from Base44 — we only render a solid swatch when the string is a
 *  valid CSS color, otherwise we degrade to the text-only custom rendering. */
function isValidHex(s) {
  return typeof s === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s.trim());
}

/**
 * Item-level color resolver — Base44's new `custom_paint_*` fields take
 * precedence over the generic `color` field, but everything else is handled
 * by the existing `resolveColor(rawColor, sku)`.
 *
 * Precedence (2026-06 handoff):
 *   1. `item.custom_paint_name` / `custom_paint_code` / `custom_paint_hex`
 *      → display as `"Name · Code"` (or whichever is present), swatch from
 *        `custom_paint_hex` when it's a valid #RRGGBB / #RGB, always marked
 *        `isCustom: true`.
 *   2. `item.color` — canonical name / 3-letter code / free-text (see
 *      `resolveColor`).
 *   3. SKU-based fallback when `color` is empty.
 *
 * Safe on partial data: any of the three custom_paint_* fields can be null.
 * We only trigger the custom-paint path when at least ONE of name/code is
 * non-empty. A hex without a name/code is ignored (there's nothing to label
 * the swatch with).
 */
export function resolveItemColor(item) {
  if (!item) return { text: "", hex: null, isCustom: false, isSkuFallback: false };

  const name = typeof item.custom_paint_name === "string" ? item.custom_paint_name.trim() : "";
  const code = typeof item.custom_paint_code === "string" ? item.custom_paint_code.trim() : "";
  const hex  = typeof item.custom_paint_hex  === "string" ? item.custom_paint_hex.trim()  : "";

  if (name || code) {
    // Compose "Name · Code" when both are distinct. When Base44 sends the
    // same value in both fields (common when they've only got one string
    // to work with), collapse to a single display value — matches backend's
    // dedupe of `color`. Comparison is case-insensitive to catch minor
    // whitespace/casing drift.
    const same = name && code && name.trim().toLowerCase() === code.trim().toLowerCase();
    const text = same ? name : [name, code].filter(Boolean).join(" · ");
    return {
      text,
      hex: isValidHex(hex) ? hex : null,
      isCustom: true,
      isSkuFallback: false,
    };
  }

  return resolveColor(item.color, item.sku);
}

// Set of canonical color names — matches Cabinet Depot's ~60 stock finishes.
// Derived from SKU_COLOR_CODES so a single edit updates both the SKU-code
// path and the canonical-detection path. Comparisons are case-insensitive
// via normalization to uppercase.
const CANONICAL_NAMES_UPPER = (() => {
  const set = new Set();
  for (const meta of Object.values(SKU_COLOR_CODES)) {
    if (meta && meta.name) set.add(meta.name.toUpperCase());
  }
  return set;
})();

/**
 * Unified color resolution — the single source of truth for how the warehouse
 * app decides what to show for an item's color, matching Base44's contract
 * (2026-06 handoff):
 *
 *   1. Base44's `color` field takes absolute precedence — never dropped.
 *      • 3-letter SKU-style code ("WHT", "GRA")             → canonical
 *      • Exact-match canonical name ("White", "Graphite")   → canonical
 *      • Anything else (paint spec, drawing ref, free-text) → CUSTOM verbatim
 *   2. Only when `color` is empty/null do we fall back to SKU inference.
 *
 * "Exact match" means the full string (after trimming) equals a canonical
 * name — case-insensitive but NOT substring-based. This is critical: a value
 * like "SW 7008 Alabaster" must NOT be treated as canonical just because
 * "Alabaster" appears somewhere inside — that's exactly the kind of silent
 * normalization loss the Base44 team's fix targeted.
 *
 * Returns:
 *   {
 *     text:          string  — the label to display (may be empty)
 *     hex:           string? — swatch color, or null for custom / unknown
 *     isCustom:      bool    — true when color was a custom / free-text value
 *     isSkuFallback: bool    — true when we inferred from the SKU
 *   }
 */
export function resolveColor(rawColor, sku) {
  const raw = typeof rawColor === "string" ? rawColor.trim() : "";

  if (raw) {
    const upper = raw.toUpperCase();
    // Direct 3-letter SKU code (e.g. "WHT") — normalize to friendly name.
    if (/^[A-Z]{2,4}$/.test(upper) && SKU_COLOR_CODES[upper]) {
      const meta = SKU_COLOR_CODES[upper];
      return { text: meta.name, hex: meta.hex, isCustom: false, isSkuFallback: false };
    }
    // Exact canonical name match (e.g. "White", "Antique White").
    if (CANONICAL_NAMES_UPPER.has(upper)) {
      const hex = resolveColorSwatch(raw);
      return { text: raw, hex, isCustom: false, isSkuFallback: false };
    }
    // Everything else = custom paint / spec reference / drawing note —
    // render VERBATIM with no swatch and mark it custom so the UI can
    // display the amber CUSTOM pill.
    return { text: raw, hex: null, isCustom: true, isSkuFallback: false };
  }

  // color was empty — SKU-based fallback (legacy behavior).
  const fromSku = resolveColorFromSku(sku);
  if (fromSku) {
    return { text: fromSku.name, hex: fromSku.hex, isCustom: false, isSkuFallback: true };
  }
  return { text: "", hex: null, isCustom: false, isSkuFallback: false };
}
